import React from 'react';
import { useTranslation } from 'react-i18next';
import { colorForCategory, labelForCategory, eventDisplayTitle } from './constants';
import { minutesToTop, eventHeight, formatMinutes } from './timeUtils';
import { eventColorClassAndStyle } from '../planning/colorUtils';

export default function CalendarEvent({
  event,
  layout,
  selected,
  isMoving,
  isResizing,
  isCopySource,
  onPointerDown,
  onContextMenu,
  onResizePointerDown,
}) {
  const { t } = useTranslation('planning');
  const color = colorForCategory(event.category);
  const colorMeta = eventColorClassAndStyle(color);
  const top = minutesToTop(event.start);
  const height = eventHeight(event.start, event.end);
  const title = String(event.title || '').trim();
  const displayTitle = eventDisplayTitle(event);
  const isMultiDay = !!event.isMultiDay;
  const spanTip =
    isMultiDay && (event.rangeStart || event.date) && (event.rangeEnd || event.endDate)
      ? `${t('multiDayEvent')} · ${event.rangeStart || event.date} → ${event.rangeEnd || event.endDate}`
      : undefined;

  const style = {
    top: `${top}px`,
    height: `${height}px`,
    ...(layout || { left: '2px', right: '2px', width: 'auto' }),
    ...colorMeta.style,
  };

  const multiClass = isMultiDay
    ? ` is-multi-day${event.continuesFromPrev ? ' continues-prev' : ''}${
        event.continuesToNext ? ' continues-next' : ''
      }`
    : '';

  return (
    <div
      className={`calendar-event ${colorMeta.className}${selected ? ' is-selected' : ''}${
        isMoving ? ' is-moving' : ''
      }${isResizing ? ' is-resizing' : ''}${isCopySource ? ' is-copy-source' : ''}${multiClass}`}
      style={style}
      data-event-id={event.id}
      title={spanTip}
      onPointerDown={onPointerDown}
      onContextMenu={onContextMenu}
    >
      {isMultiDay ? (
        <div className="event-multi-day-badge" aria-label={t('multiDayEvent')}>
          {event.continuesFromPrev ? (
            <span className="material-symbols-outlined event-multi-day-arrow">keyboard_arrow_up</span>
          ) : null}
          <span className="material-symbols-outlined">date_range</span>
          <span className="event-multi-day-label">{t('multiDay')}</span>
          {event.continuesToNext ? (
            <span className="material-symbols-outlined event-multi-day-arrow">keyboard_arrow_down</span>
          ) : null}
        </div>
      ) : null}
      <div className="event-body">
        <span className="event-category">{labelForCategory(event.category)}</span>
        {title ? <h3 className="event-title">{displayTitle}</h3> : null}
        <p className="event-time">
          <span className="material-symbols-outlined">schedule</span>
          <span className="event-time-text">
            {formatMinutes(event.start)} - {formatMinutes(event.end)}
          </span>
        </p>
      </div>
      {!isMultiDay ? (
        <div
          className="event-resize-handle"
          onPointerDown={(e) => {
            e.stopPropagation();
            onResizePointerDown?.(e);
          }}
        />
      ) : null}
    </div>
  );
}
